"use client";

import { useContent } from "@/hooks/useContent";
import type { Bilingual, Locale } from "@/types/home";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { useLocale } from "next-intl";

interface ContactContent {
  address: Bilingual;
  lat: number;
  lng: number;
}

/** Interactive Google map framed to the Mansoura HQ, with a branded pin + info card. */
export default function ContactMap() {
  const locale = useLocale() as Locale;
  const ar = locale === "ar";
  const { data } = useContent<ContactContent>("contact");

  const lat = data?.lat ?? 31.0409;
  const lng = data?.lng ?? 31.3785;
  const embed = `https://www.google.com/maps?q=${lat},${lng}&z=15&hl=${locale}&output=embed`;
  const link = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  const address = data?.address?.[locale] ?? "";

  return (
    <section className="border-t border-black/10 dark:border-white/10">
      <div className="mx-auto max-w-360 px-6 py-16 lg:px-10">
        <span className="text-[11px] uppercase tracking-[0.22em] text-accent">{ar ? "زُر مقرنا" : "Visit us"}</span>
        <h2 className="mt-2 font-bebas text-[clamp(1.8rem,4vw,3rem)] uppercase leading-none text-ash dark:text-white">
          {ar ? "المقر الرئيسي — المنصورة" : "Headquarters — Mansoura"}
        </h2>

        <div className="relative mt-8 overflow-hidden border border-black/10 dark:border-white/10">
          {/* Real, interactive map */}
          <iframe
            title={ar ? "خريطة مقر تحاور بالمنصورة" : "Tahawer Mansoura HQ map"}
            src={embed}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="h-[380px] w-full grayscale-[0.35] contrast-[1.05] transition-all duration-500 hover:grayscale-0 md:h-[460px]"
          />

          {/* Branded pin marker (centred over the office, non-blocking) */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={{ y: -14, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 260, damping: 18 }}
              className="relative -translate-y-6"
            >
              <span className="absolute left-1/2 top-full h-3 w-3 -translate-x-1/2 -translate-y-1.5 rotate-45 bg-accent" />
              <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-accent text-white shadow-lg shadow-accent/40">
                <MapPin className="h-6 w-6" />
              </div>
              <span className="absolute left-1/2 top-1/2 -z-10 h-12 w-12 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full bg-accent/40" />
            </motion.div>
          </div>

          {/* Info card */}
          <div className="absolute bottom-4 inset-s-4 max-w-xs border border-black/10 bg-white/90 p-5 backdrop-blur-md dark:border-white/10 dark:bg-ash/90">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
              <MapPin className="h-3.5 w-3.5" />
              {ar ? "المقر الرئيسي" : "Headquarters"}
            </div>
            <div className="mt-2 font-semibold text-ash dark:text-white">{ar ? "المنصورة" : "Mansoura"}</div>
            <div className="text-sm text-ash/60 dark:text-white/60">{address}</div>
            <div className="mt-1 text-xs text-ash/45 dark:text-white/45">{ar ? "السبت – الخميس · ٩ص – ٦م" : "Sat – Thu · 9am – 6pm"}</div>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-ash transition-colors hover:text-accent dark:text-white dark:hover:text-accent"
            >
              {ar ? "الاتجاهات" : "Get directions"}
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
